// src/api/piiVault.ts

import { apiJson } from "./client";

/**
 * Something that still uses the vault and blocks a disconnect. `kind` is one of
 * PII_VAULT_CONSUMER_KIND; `erasure_request` and `vault_caller` carry a generic
 * name, so the UI pairs every name with a label for its kind.
 */
export type PiiVaultConsumer = {
  kind: string;
  id: string;
  name: string;
};

export const PII_VAULT_CONSUMER_KIND = {
  SOURCE: "source",
  DESTINATION: "destination",
  ERASURE_REQUEST: "erasure_request",
  VAULT_CALLER: "vault_caller",
} as const;

/**
 * PII-vault provisioning status for the current account (mirrors the backend's
 * `PiiVaultStatusResponse`). The customer never acts on the internal vault SA,
 * so it isn't exposed. `provisioning_status` is one of PII_VAULT_STATUS
 * (lowercase — distinct from the DWH status casing); an unknown value must be
 * rendered neutrally, never as `not_started`.
 */
export type PiiVaultStatus = {
  provisioning_status: string;
  pii_project_id: string | null;
  error_message: string | null;
  /** VERITY_MANAGED | CUSTOMER_OWNED | null — null means Verity-managed. */
  storage_mode: string | null;
  /** The teardown/provisioning step that failed, e.g. `erase_pii` (BYO). */
  failed_step?: string | null;
  /** What still uses the vault. Only filled while `active`. */
  consumers?: PiiVaultConsumer[];
};

export const PII_VAULT_STATUS = {
  NOT_STARTED: "not_started",
  IN_PROGRESS: "in_progress",
  ACTIVE: "active",
  FAILED: "failed",
  /** Disconnect accepted; the teardown runs in the background. */
  DEPROVISIONING: "deprovisioning",
  /** The teardown stopped at `failed_step`; a retry is allowed. */
  DEPROVISION_FAILED: "deprovision_failed",
} as const;

export const PII_VAULT_STORAGE_MODE = {
  VERITY_MANAGED: "VERITY_MANAGED",
  CUSTOMER_OWNED: "CUSTOMER_OWNED",
} as const;

/** True once the vault is provisioned and usable (gates rely on this). */
export function isPiiVaultReady(status: PiiVaultStatus | null | undefined): boolean {
  return status?.provisioning_status === PII_VAULT_STATUS.ACTIVE;
}

/** GET /accounts/pii-vault/status */
export async function getPiiVaultStatus(): Promise<PiiVaultStatus> {
  return apiJson<PiiVaultStatus>("/accounts/pii-vault/status", {
    unwrapData: false,
  });
}

/**
 * POST /accounts/pii-vault/provision — start Verity-managed provisioning
 * (OWNER/ADMIN). Optional custom project name; the backend scopes to the JWT
 * account. Returns 202 + the current status; callers then poll getPiiVaultStatus.
 */
export async function provisionPiiVault(
  customProjectName?: string,
): Promise<PiiVaultStatus> {
  return apiJson<PiiVaultStatus>("/accounts/pii-vault/provision", {
    method: "POST",
    unwrapData: false,
    body: customProjectName
      ? JSON.stringify({ custom_project_name: customProjectName })
      : undefined,
  });
}

/**
 * POST /accounts/pii-vault/connect — connect a customer-owned (BYO) project.
 * `acceptTerms` records the shared-responsibility acceptance server-side; the
 * BFF rejects the connect (422) without it.
 */
export async function connectPiiVault(input: {
  bqProjectId: string;
  bqLocation: string;
  acceptTerms: boolean;
}): Promise<PiiVaultStatus> {
  return apiJson<PiiVaultStatus>("/accounts/pii-vault/connect", {
    method: "POST",
    unwrapData: false,
    body: JSON.stringify({
      bq_project_id: input.bqProjectId,
      bq_location: input.bqLocation,
      accept_terms: input.acceptTerms,
    }),
  });
}

export type PiiVaultGrantInstructions = {
  cloud_shell_url: string;
  gcloud_commands: string[];
  pii_vault_sa_email: string;
  pii_project_id: string;
  audit_logs_note: string;
};

/** GET /accounts/pii-vault/grant-instructions (BYO). */
export async function getPiiVaultGrantInstructions(): Promise<PiiVaultGrantInstructions> {
  return apiJson<PiiVaultGrantInstructions>(
    "/accounts/pii-vault/grant-instructions",
    { unwrapData: false },
  );
}

/** POST /accounts/pii-vault/validate (BYO) — activate once the grant is done. */
export async function validatePiiVault(): Promise<PiiVaultStatus> {
  return apiJson<PiiVaultStatus>("/accounts/pii-vault/validate", {
    method: "POST",
    unwrapData: false,
  });
}

/**
 * POST /accounts/pii-vault/disconnect (OWNER/ADMIN) — start the teardown of the
 * vault in any state. Returns 202 + the status, which is `deprovisioning` until
 * the background teardown ends in `not_started` or `deprovision_failed`, so
 * callers poll getPiiVaultStatus. An `active` vault needs `confirmProjectId`
 * equal to its `pii_project_id` (422 otherwise). Remaining consumers → 409 whose
 * `ApiError.detail` is `{ message, consumers: PiiVaultConsumer[] }`.
 */
export async function disconnectPiiVault(
  confirmProjectId?: string,
): Promise<PiiVaultStatus> {
  return apiJson<PiiVaultStatus>("/accounts/pii-vault/disconnect", {
    method: "POST",
    unwrapData: false,
    body: JSON.stringify(
      confirmProjectId !== undefined
        ? { confirm_project_id: confirmProjectId }
        : {},
    ),
  });
}

/** The consumer list from a disconnect's 409 `detail`, or null for any other error. */
export function consumersFromDisconnectError(
  err: unknown,
): PiiVaultConsumer[] | null {
  const e = err as { status?: unknown; detail?: unknown } | null;
  if (!e || e.status !== 409) return null;
  const consumers = (e.detail as { consumers?: unknown } | null)?.consumers;
  return Array.isArray(consumers) ? (consumers as PiiVaultConsumer[]) : null;
}
